import axios, { CancelTokenSource } from 'axios';
import CancelActionButton from 'components/CancelActionButton';
import DeleteActionButton from 'components/DeleteActionButton';
import EditActionButton from 'components/EditActionButton';
import CheckBox from 'components/final-form/CheckBox';
import {
  DropdownOption,
  mapToDropdownOptionArray,
  mapToDropdownOptionArrayWithSep,
  stringArrayToDropdownOptionArray
} from 'components/final-form/dropdownUtils';
import Input, { StyledInput } from 'components/final-form/Input';
import Select, { PxDropdownSc } from 'components/final-form/Select';
import TextArea from 'components/final-form/TextArea';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import SaveActionButton from 'components/SaveActionButton';
import { FormApi } from 'final-form';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, DropdownProps, Grid, Header, Table } from 'semantic-ui-react';
import { addArticleGroup, editArticleGroup } from 'services/articleGroupServices';
import { getAllShippableMedicaments } from 'services/medicamentServices';
import { InterventionExternalCode } from 'ts-types/api.enums';
import { ArticleGroupItem, TestLaboratoryDto, UpsertArticleGroupDto } from 'ts-types/api.types';
import {
  composeValidators,
  isValueValidNumber,
  mustBeNumber,
  mustBePositiveNumber,
  mustNotBeZero,
  required
} from 'utils/validatorUtils';

const addButton = {
  marginBottom: 10,
  marginTop: 10
};

interface Props extends MessageSourceProps {

  toggleAddEditModal: () => void,
  onCancel: () => void,
  onSave: () => void,
  articleGroupId?: number,
  articleGroup?: UpsertArticleGroupDto,
  testLaboratories: Array<TestLaboratoryDto>
}

interface State {
  medicaments: Array<DropdownOption>,
  articleGroupItemIx?: number,
  selectedMedicament?: number,
  quantity?: string,
  externalCodeOptions: DropdownOption[],
  laboratoryOptions: Array<DropdownOption>,
  errorMessage?: string,
  errors: Array<boolean>,
  cancelTokenSource: CancelTokenSource
}

class UpsertArticleGroupForm extends Component<Props, State> {

  private initialValues: Partial<UpsertArticleGroupDto>;

  constructor(props: Props) {
    super(props);

    const {getMessage} = this.props;

    const cancelTokenSource = axios.CancelToken.source();

    const externalCodeOptions = stringArrayToDropdownOptionArray(
        Object.values(InterventionExternalCode), getMessage, "enum.InterventionExternalCode");

    this.state = {
      medicaments: [],
      externalCodeOptions,
      laboratoryOptions: mapToDropdownOptionArray(props.testLaboratories, "name", "id", "id"),
      errors: [false, false],
      cancelTokenSource
    }

    getAllShippableMedicaments(cancelTokenSource).then(response => {
      this.setState({
        medicaments: mapToDropdownOptionArrayWithSep(response, ["description", "externalOrderNumber"], "id")
      })
    });

    const {articleGroupId, articleGroup} = this.props;

    this.initialValues = {
      articleGroupItems: [],
      active: true,
      packageQuantity: 1
    };

    if (articleGroupId) {
      this.initialValues = {
        ...articleGroup
      };
    }
  }

  validateFields = () => {
    const {quantity, selectedMedicament} = this.state;

    let errors: Array<boolean> = [];

    const mustBeNumber = !((quantity || quantity === "0") && isValueValidNumber(quantity));

    errors[0] = !(selectedMedicament !== undefined);
    errors[1] = mustBeNumber;

    this.setState({errors: errors});

    return errors.includes(true);
  }

  handleSubmit = async (values: Partial<UpsertArticleGroupDto>) => {

    let saveValues: Partial<UpsertArticleGroupDto> = {...values};

    const {articleGroupId, getMessage, onSave} = this.props;
    const {articleGroupItemIx, cancelTokenSource} = this.state;

    let validArticleGroupItems = true;

    if (values.articleGroupItems && values.articleGroupItems.length > 0) {
      if (articleGroupItemIx !== undefined) {
        validArticleGroupItems = !this.validateFields();
      }
    }

    if (!validArticleGroupItems) {

      let errorMessage = "Please select medicament and quantity";
      this.setErrorMessage(errorMessage);
    }

    if (validArticleGroupItems && articleGroupItemIx !== undefined) {
      this.setErrorMessage("Please save the item that you are editing")
    }

    if (validArticleGroupItems && articleGroupItemIx === undefined) {
      if (articleGroupId) {
        await editArticleGroup(articleGroupId, saveValues, cancelTokenSource)
            .then(onSave)
            .catch((e: any) => this.handleError(e.response.data))
      } else {
        await addArticleGroup(saveValues, cancelTokenSource)
            .then(onSave)
            .catch((e: any) => this.handleError(e.response.data))
      }
    }
  };

  handleError(error: any) {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors: Array<string> = [];

    const violations: Array<any> = error.violations;
    let knowErrorFound = false;
    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          knowErrorFound = true;
          this.setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    }

    if (!knowErrorFound) {
      if (knownErrors.includes(errorCode)) {
        this.setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        this.setErrorMessage(getMessage('error.general'));
      }
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  onMedicamentChange = (e: any, {value}: DropdownProps) => {
    if (typeof value === "number") {
      this.setState({
        selectedMedicament: value
      })
    }
  };

  onQuantityChange = (e: any) => {
    this.setState({
      quantity: e.target.value
    })
  };

  addArticleGroupItem = (e: any, form: FormApi, values: any) => {

    e.preventDefault();

    let articleGroupItems = values.articleGroupItems ? [...values.articleGroupItems] : [];

    const newArticleGroupItem = {
      medicamentId: undefined,
      quantity: undefined
    }

    articleGroupItems.push(newArticleGroupItem);

    form.change("articleGroupItems", articleGroupItems);

    this.setState({
      articleGroupItemIx: articleGroupItems.length - 1
    })
  }

  editArticleGroupItem = (values: any, ix: number) => {

    const {medicaments} = this.state;
    const articleGroupItems = values.articleGroupItems;

    let quantity = "";
    let selectedMedicament = medicaments.length > 0 ? medicaments[0].value : undefined;

    if (articleGroupItems && articleGroupItems.length > 0) {

      const articleGroupItem = articleGroupItems.find((agt: ArticleGroupItem, index: number) => ix === index);

      if (articleGroupItem) {
        quantity = articleGroupItem.quantity;
        selectedMedicament = articleGroupItem.medicamentId;
      }
    }

    this.setState({
      quantity: quantity,
      selectedMedicament: selectedMedicament,
      articleGroupItemIx: ix
    });
  }

  deleteArticleGroupItem = (values: any, form: FormApi, ix: number) => {

    if (ix > -1) {
      let articleGroupItems = values.articleGroupItems ? [...values.articleGroupItems] : [];

      if (articleGroupItems.length > 0) {

        articleGroupItems = articleGroupItems.filter((agt: ArticleGroupItem, index: number) => ix !== index);
        form.change("articleGroupItems", articleGroupItems);
      }
    }
  }

  saveArticleGroupItem = (values: any, form: FormApi, ix: number) => {

    const {quantity, selectedMedicament} = this.state;

    if (!this.validateFields()) {
      let articleGroupItems = values.articleGroupItems ? [...values.articleGroupItems] : [];

      if (ix > -1) {
        articleGroupItems[ix] = {...articleGroupItems[ix], medicamentId: selectedMedicament, quantity: quantity};
      } else {
        const newArticleGroupItem = {
          medicamentId: selectedMedicament,
          quantity
        }
        articleGroupItems.push(newArticleGroupItem);
      }

      form.change("articleGroupItems", articleGroupItems);

      this.setState({
        articleGroupItemIx: undefined,
        quantity: undefined,
        selectedMedicament: undefined
      })
    }
  }

  cancelEditArticleGroupItem = (values: any, form: FormApi, ix: number) => {
    const {quantity, selectedMedicament} = this.state;

    const articleGroupItems = [...values.articleGroupItems];
    const articleGroupItem = articleGroupItems[ix];

    if (((quantity === undefined || quantity === "" || quantity === "0") && selectedMedicament === undefined)
        || (articleGroupItem
            && ((articleGroupItem.quantity === undefined || articleGroupItem.quantity === "" || articleGroupItem.quantity === 0)
                && articleGroupItem.medicamentId === undefined))) {

      this.deleteArticleGroupItem(values, form, ix);
    }

    this.setState({
      articleGroupItemIx: undefined,
      quantity: undefined,
      selectedMedicament: undefined
    })
  }

  render(): React.ReactNode {

    return (
        <FinalForm
            onSubmit={(values) => this.handleSubmit(values)}
            initialValues={this.initialValues}
            subscription={{values: true, pristine: true, submitting: true}}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({handleSubmit, submitting, values, form}: FormRenderProps): React.ReactNode => {

    const {getMessage, onCancel} = this.props;
    const {externalCodeOptions, articleGroupItemIx, errorMessage, laboratoryOptions} = this.state;

    const disableAddButton = submitting || articleGroupItemIx !== undefined;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
              {errorMessage
              && (
                  <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                    {errorMessage}
                  </StyledErrorMessage>
              )
              }
              <PraxFormGrid>
                <Grid.Row>
                  <Grid.Column width={3}>
                    <label htmlFor="description">{getMessage("articleGroup.description")}</label>
                  </Grid.Column>
                  <Grid.Column width={8}>
                    <Field
                        fluid
                        id="description"
                        name="description"
                        component={Input}
                        validate={required}
                        autoFocus
                    />
                  </Grid.Column>
                  <Grid.Column width={5} textAlign="right">
                      <Field
                          name="active"
                          component={CheckBox}
                          label={getMessage("articleGroup.active")}
                          toggle
                      />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={3}>
                    <label htmlFor="externalOrderNumber">{getMessage("externalOrderNumber")}</label>
                  </Grid.Column>
                  <Grid.Column width={8}>
                    <Field
                        fluid
                        id="externalOrderNumber"
                        name="externalOrderNumber"
                        component={Input}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={3}>
                    <label htmlFor="externalCode">{getMessage("articleGroup.externalCode")}</label>
                  </Grid.Column>
                  <Grid.Column width={8}>
                    <Field
                        id="externalCode"
                        name="externalCode"
                        component={Select}
                        options={externalCodeOptions}
                        validate={required}
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={3}>
                    <label htmlFor="testLaboratoryId">{getMessage("articleGroup.testlaboratory")}</label>
                  </Grid.Column>
                  <Grid.Column width={8}>
                    <Field
                        name="testLaboratoryId"
                        component={Select}
                        options={laboratoryOptions}
                        clearable
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={3}>
                    <label htmlFor="packageQuantity">{getMessage("articleGroup.packageQuantity")}</label>
                  </Grid.Column>
                  <Grid.Column width={8}>
                    <Field
                        name="packageQuantity"
                        component={Input}
                        validate={composeValidators(required, mustBeNumber, mustNotBeZero, mustBePositiveNumber)}
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={16}>
                    {this.renderArticleGroupItemsSection(form, values)}
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column>
                    <Button
                        type="button"
                        compact
                        style={addButton}
                        onClick={(e) => this.addArticleGroupItem(e, form, values)}
                        disabled={disableAddButton}
                    >
                      {getMessage('button.add')}
                    </Button>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column>
                    <Field
                        name="remark"
                        component={TextArea}
                        rows={2}
                        placeholder={getMessage("remarks")}
                    />
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>

            <FormButtonsRow>
              <Button compact type="submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  };

  renderArticleGroupItemsSection = (form: FormApi, values: any): React.ReactNode => {
    const {getMessage} = this.props;
    const {medicaments, selectedMedicament, quantity, articleGroupItemIx, errors} = this.state;
    const articleGroup = {...values};

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column>
              <Header as="h5" style={{marginTop: "1rem"}}>
                {getMessage("articleGroup.items")}
              </Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
                <Table celled selectable size="small">
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>{getMessage('articleGroupItem.material')}</Table.HeaderCell>
                      <Table.HeaderCell>{getMessage('articleGroupItem.quantity')}</Table.HeaderCell>
                      <Table.HeaderCell textAlign={"center"}>{getMessage('actions')}</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {
                      articleGroup && articleGroup.articleGroupItems &&
                      articleGroup.articleGroupItems.map((articleGroupItem: ArticleGroupItem, index: number) => {

                        const medicament = medicaments.find(m => m.value === articleGroupItem.medicamentId);
                        const medicamentDesc = medicament ? medicament.text : "";
                        const editing = articleGroupItemIx === index;

                        return (
                            <Table.Row key={index}>
                              <Table.Cell width={8}>
                                {editing
                                    ? <PxDropdownSc
                                        selection
                                        value={selectedMedicament}
                                        options={medicaments}
                                        onChange={this.onMedicamentChange}
                                        error={errors[0]}
                                        fluid
                                    />
                                    : medicamentDesc
                                }
                              </Table.Cell>
                              <Table.Cell width={4}>
                                {
                                  editing
                                      ? <StyledInput
                                          value={quantity}
                                          onChange={(e: any) => this.onQuantityChange(e)}
                                          touched={errors[1]}
                                          error={errors[1]}
                                          fluid
                                      />
                                      : articleGroupItem.quantity
                                }
                              </Table.Cell>
                              <Table.Cell width={2} textAlign={"center"}>
                                {editing
                                    ? <React.Fragment>
                                      <SaveActionButton hoverMessage={getMessage('button.save')}
                                                        onConfirm={() =>
                                                            this.saveArticleGroupItem(values, form, index)}
                                      />
                                      <CancelActionButton hoverMessage={getMessage('button.cancel')}
                                                          onConfirm={() => this.cancelEditArticleGroupItem(values, form, index)}/>
                                    </React.Fragment>
                                    : <React.Fragment>
                                      <EditActionButton
                                          hoverMessage={getMessage('msgEdit')}
                                          onConfirm={() => this.editArticleGroupItem(values, index)}
                                      />
                                      <DeleteActionButton
                                          hoverMessage={getMessage("msgDelete")}
                                          onConfirm={() => this.deleteArticleGroupItem(values, form, index)}
                                      />
                                    </React.Fragment>
                                }
                              </Table.Cell>
                            </Table.Row>
                        );
                      })
                    }
                  </Table.Body>
                </Table>
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    );
  }
}

export default withMessages(UpsertArticleGroupForm);
